import { useState } from 'react';

import { InputGroupInput } from '@/components/shadcn/ui/input-group';
import { cn } from '@/lib/cn';

import { useFieldContext } from './form';
import { getFieldError } from './get-field-error';

interface FormInputGroupInputProps
  extends Omit<
    React.ComponentProps<typeof InputGroupInput>,
    'value' | 'onChange' | 'name'
  > {
  showErrorMessage?: boolean;
}

export function FormInputGroupInput({
  showErrorMessage = true,
  type,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  className,
  ...props
}: FormInputGroupInputProps) {
  const field = useFieldContext<string | number>();

  // number 타입 전용 로컬 state — 포커스 중 사용자 입력값을 그대로 유지 (0 삭제 가능)
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (type === 'number') {
      setLocalValue(value);
      const num = Number(value);
      field.handleChange(value === '' || Number.isNaN(num) ? 0 : num);
    } else {
      field.handleChange(value);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'number') {
      setLocalValue(String(field.state.value ?? ''));
      setIsFocused(true);
    }
    onFocusProp?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'number') {
      // 블러 시 정수가 아닌 경우 0으로 정규화
      const num = Number(localValue);
      const finalValue = Number.isInteger(num) ? num : 0;
      field.handleChange(finalValue);
      field.handleBlur();
      setIsFocused(false);
    }
    onBlurProp?.(e);
  };

  // number 타입: 포커스 중에는 로컬 값, 아닐 때는 form state 값
  const displayValue =
    type === 'number'
      ? isFocused
        ? localValue
        : String(field.state.value ?? '')
      : (field.state.value ?? '');

  return (
    <>
      <InputGroupInput
        id={field.name}
        name={field.name}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        type={type}
        className={cn(
          // number input spinner 숨김
          type === 'number' &&
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          className,
        )}
        {...props}
      />
      {showErrorMessage && field.state.meta.errors.length > 0 && (
        <span className="text-destructive text-sm">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      )}
    </>
  );
}
